"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { getSetSummaries } from "./lib/opIndex";
import type { IndexedSetSummary } from "./lib/opIndex";

const PAGE_ID_PREFIX = "home";

const FOOTER_REVEAL_SCROLL_PX = 64;

function latestReleases(count: number): IndexedSetSummary[] {
  const withDate = getSetSummaries().filter((s) => s.releaseDate);
  withDate.sort((a, b) =>
    (b.releaseDate ?? "").localeCompare(a.releaseDate ?? ""),
  );
  return withDate.slice(0, count);
}

export default function HomePage() {
  const [footerVisible, setFooterVisible] = useState(false);
  /** Newest set → "Preview"; next few → "Latest releases" (set codes hidden to avoid wrap). */
  const releases = useMemo(() => latestReleases(5), []);
  const previewRelease = releases[0];
  const otherReleases = releases.slice(1);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY >= FOOTER_REVEAL_SCROLL_PX) setFooterVisible(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      id={`${PAGE_ID_PREFIX}-page`}
      className="flex min-h-screen flex-col bg-slate-950 text-slate-50"
    >
      <Header idPrefix={`${PAGE_ID_PREFIX}-header`} />

      <main
        id={`${PAGE_ID_PREFIX}-main`}
        className="flex min-h-[calc(100vh+5rem)] flex-1 flex-col items-center justify-center px-4 pb-8"
      >
        <div
          id={`${PAGE_ID_PREFIX}-hero`}
          className="w-full max-w-xl space-y-6 text-center"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            id={`${PAGE_ID_PREFIX}-hero-logo`}
            src="/logo.svg"
            alt="Punk Records"
            className="logo-themed mx-auto h-16 w-16"
          />
          <h1
            id={`${PAGE_ID_PREFIX}-hero-title`}
            className="text-3xl font-semibold text-slate-100 md:text-4xl"
          >
            Punk Records! Unveil the missing history...
          </h1>
          <form
            id={`${PAGE_ID_PREFIX}-search-form`}
            className="mt-4"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget as HTMLFormElement;
              const data = new FormData(form);
              const q = (data.get("q") as string | null)?.trim() ?? "";
              const params = new URLSearchParams();
              if (q) params.set("q", q);
              window.location.href = `/search?${params.toString()}`;
            }}
          >
            <input
              id={`${PAGE_ID_PREFIX}-search-input`}
              name="q"
              type="text"
              enterKeyHint="search"
              placeholder='Search cards, e.g. set:EB01 t:leader color:red cost<=4'
              className="w-full rounded-full border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </form>
          <div
            id={`${PAGE_ID_PREFIX}-nav-links`}
            className="flex flex-wrap justify-center gap-3 text-sm"
          >
            <Link href="/search" className="text-blue-400 hover:underline">
              Search
            </Link>
            <Link href="/syntax" className="text-blue-400 hover:underline">
              Syntax
            </Link>
            <Link href="/sets" className="text-blue-400 hover:underline">
              Sets
            </Link>
            <Link href="/advanced" className="text-blue-400 hover:underline">
              Advanced
            </Link>
            <Link href="/deckbuild" className="text-blue-400 hover:underline">
              Deckbuild
            </Link>
            <Link href="/meta" className="text-blue-400 hover:underline">
              Meta
            </Link>
            <Link href="/collection" className="text-blue-400 hover:underline">
              Collection
            </Link>
          </div>

          {releases.length > 0 && (
            <div
              id={`${PAGE_ID_PREFIX}-latest-releases`}
              className="mx-auto w-full max-w-md border-t border-slate-800/80 pt-5 text-left"
            >
              {previewRelease && (
                <section aria-labelledby={`${PAGE_ID_PREFIX}-preview-heading`}>
                  <h2
                    id={`${PAGE_ID_PREFIX}-preview-heading`}
                    className="mb-2 text-center text-2xs font-semibold uppercase tracking-wide text-slate-400"
                  >
                    Preview
                  </h2>
                  <ul className="space-y-2 text-sm text-slate-400">
                    <li
                      key={previewRelease.code}
                      id={`${PAGE_ID_PREFIX}-release-${previewRelease.code}`}
                      className="flex gap-2"
                    >
                      <span className="select-none text-slate-500" aria-hidden>
                        •
                      </span>
                      <div className="min-w-0 flex-1">
                        <Link
                          href={`/search?q=${encodeURIComponent(`set:${previewRelease.code}`)}`}
                          className="block truncate text-blue-400 hover:underline"
                          title={previewRelease.name}
                        >
                          <span className="text-slate-100">{previewRelease.name}</span>
                        </Link>
                      </div>
                      <span className="shrink-0 text-2xs text-slate-400 tabular-nums">
                        {previewRelease.releaseDate}
                      </span>
                    </li>
                  </ul>
                </section>
              )}

              {otherReleases.length > 0 && (
                <section
                  className={previewRelease ? "mt-5" : ""}
                  aria-labelledby={`${PAGE_ID_PREFIX}-latest-heading`}
                >
                  <h2
                    id={`${PAGE_ID_PREFIX}-latest-heading`}
                    className="mb-2 text-center text-2xs font-semibold uppercase tracking-wide text-slate-400"
                  >
                    Latest releases
                  </h2>
                  <ul className="space-y-2 text-sm text-slate-400">
                    {otherReleases.map((s) => (
                      <li
                        key={s.code}
                        id={`${PAGE_ID_PREFIX}-release-${s.code}`}
                        className="flex gap-2"
                      >
                        <span className="select-none text-slate-500" aria-hidden>
                          •
                        </span>
                        <div className="min-w-0 flex-1">
                          <Link
                            href={`/search?q=${encodeURIComponent(`set:${s.code}`)}`}
                            className="block truncate text-blue-400 hover:underline"
                            title={s.name}
                          >
                            <span className="text-slate-100">{s.name}</span>
                          </Link>
                        </div>
                        <span className="shrink-0 text-2xs text-slate-400 tabular-nums">
                          {s.releaseDate}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      </main>

      <div
        className={
          footerVisible
            ? "opacity-100 transition-opacity duration-500 ease-out"
            : "pointer-events-none max-h-0 overflow-hidden opacity-0"
        }
        aria-hidden={!footerVisible}
      >
        <Footer idPrefix={`${PAGE_ID_PREFIX}-footer`} />
      </div>
    </div>
  );
}
